import { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@bonusly/design-system';

import { DoodleCanvas } from './doodle-canvas';
import { DoodleToolbar } from './doodle-toolbar';
import { hasRenderedContent } from './stroke-utils';
import { useDoodle, type DoodleStroke } from './use-doodle';

export interface DoodleModalProps {
	isOpen: boolean;
	onClose: () => void;
	/**
	 * Called with the edited strokes when the user finishes. The modal is
	 * edit-only — rasterizing/uploading happens at the doodle_card on
	 * "Add to recognition".
	 */
	onSave: (strokes: DoodleStroke[]) => void;
	/**
	 * Seed strokes to edit (the backend-generated `doodle.strokes`). When
	 * provided the canvas opens pre-populated so the user can keep drawing.
	 */
	initialStrokes?: DoodleStroke[];
}

export const DoodleModal: FC<DoodleModalProps> = ({ isOpen, onClose, onSave, initialStrokes }) => {
	const { t } = useTranslation('admin_bizy');
	const {
		tool,
		setTool,
		color,
		setColor,
		size,
		setSize,
		strokes,
		currentStroke,
		startStroke,
		addPoint,
		endStroke,
		performFill,
		undo,
		redo,
		clear,
		canUndo,
		canRedo,
		hasContent,
	} = useDoodle(initialStrokes);
	const strokesRef = useRef(strokes);
	strokesRef.current = strokes;

	const handleSave = useCallback(() => {
		// Guard on rendered content (matches the Save button's disabled state):
		// a canvas of degenerate taps/flicks has strokes but paints nothing.
		if (!hasRenderedContent(strokesRef.current)) return;
		onSave(strokesRef.current);
		onClose();
	}, [onSave, onClose]);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			const isMod = e.metaKey || e.ctrlKey;

			if (isMod && e.key === 'z' && !e.shiftKey) {
				e.preventDefault();
				undo();
				return;
			}
			if (isMod && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
				e.preventDefault();
				redo();
				return;
			}

			if (isMod || e.altKey) return;

			switch (e.key.toLowerCase()) {
				case 'b':
					setTool('brush');
					break;
				case 'e':
					setTool('eraser');
					break;
				case 'f':
					setTool('fill');
					break;
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, undo, redo, setTool]);

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="4xl" closeOnOverlayClick={false}>
			<ModalOverlay />
			<ModalContent maxW={{ base: '95vw', md: '900px' }} mx="auto">
				<ModalHeader borderBottomWidth="1px" borderColor="border.default" borderStyle="solid" color="text.default">
					<Text fontSize="lg" fontWeight="semibold">
						{t('doodle.title')}
					</Text>
				</ModalHeader>
				<ModalCloseButton size="lg" borderRadius="full" />

				<ModalBody py={4} px={{ base: 3, sm: 6 }}>
					<Box mb={4}>
						<DoodleToolbar
							tool={tool}
							color={color}
							size={size}
							canUndo={canUndo}
							canRedo={canRedo}
							onToolChange={setTool}
							onColorChange={setColor}
							onSizeChange={setSize}
							onUndo={undo}
							onRedo={redo}
							onClear={clear}
						/>
					</Box>

					<DoodleCanvas
						tool={tool}
						strokes={strokes}
						currentStroke={currentStroke}
						onStartStroke={startStroke}
						onAddPoint={addPoint}
						onEndStroke={endStroke}
						onFill={performFill}
					/>
				</ModalBody>

				<ModalFooter gap={3} pt={1} pb={4}>
					<Button variant="ghost" colorScheme="secondary" onClick={handleClose}>
						{t('doodle.cancel')}
					</Button>
					<Button colorScheme="primary" onClick={handleSave} isDisabled={!hasContent}>
						{t('doodle.save')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
